import React from 'react'
import { useNavigate } from 'react-router-dom'
import { PlusCircle, Timer, Menu } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../hooks/useAuth'
import { useLoadingBar } from '../contexts/LoadingBarContext'
import {
  type BatchSummary,
  type BatchMetrics,
  authFetch,
  fetchMatchingStatus,
  getApiBase,
} from '../lib/api'
import GlobalMetricsCards from '../components/dashboard/GlobalMetricsCards'
import { BatchListingTable } from '../components/dashboard/BatchListingTable'
import UploadBatchModal from '../components/dashboard/UploadBatchModal'
import { Sidebar } from '../components/shared/Sidebar'
import { RowsPerPageDropdown } from '../components/RowsPerPageDropdown'

const API_BASE = getApiBase()

export const DashboardOverviewPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [metrics, setMetrics] = React.useState<BatchMetrics | null>(null)
  const [metricsLoading, setMetricsLoading] = React.useState(true)

  const [batches, setBatches] = React.useState<BatchSummary[]>([])
  const [page, setPage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(1)
  const [rowsPerPage, setRowsPerPage] = React.useState(50)
  const [batchesLoading, setBatchesLoading] = React.useState(true)
  const [sortBy, setSortBy] = React.useState<'id' | 'createdAt'>('createdAt')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [cooldownRemaining, setCooldownRemaining] = React.useState<number>(0)
  /** Live status batches from GET /status (used for retryFailedOnly when batch is stopped). */
  const [statusBatches, setStatusBatches] = React.useState<
    Array<{ batchId: number; retryFailedOnly?: boolean }>
  >([])

  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false)
  const [sidebarOpen, setSidebarOpen] = React.useState(false)
  const loadingBar = useLoadingBar()

  /** Load metrics + batches for dashboard (separate APIs). */
  const loadData = React.useCallback(
    async (
      pageToLoad: number,
      limit: number,
      sortKey: 'id' | 'createdAt',
      sortDir: 'asc' | 'desc',
      showLoading: boolean = false
    ) => {
      if (showLoading) {
        loadingBar.start()
        setMetricsLoading(true)
        setBatchesLoading(true)
      }
      try {
        const combined = await fetchMatchingStatus({
          page: pageToLoad,
          limit,
          status: 'all',
          sortBy: sortKey,
          sortOrder: sortDir,
        })

        setMetrics(combined.metrics)
        if (combined.batchesList) {
          setBatches(combined.batchesList.batches)
          setPage(combined.batchesList.pagination.currentPage)
          setTotalPages(combined.batchesList.pagination.totalPages)
        }
        setCooldownRemaining(combined.cooldownRemainingSeconds ?? 0)
        setStatusBatches(
          (combined.batches ?? []).map(b => {
            const item: { batchId: number; retryFailedOnly?: boolean } = {
              batchId: b.batchId,
            }
            if (b.retryFailedOnly !== undefined)
              item.retryFailedOnly = b.retryFailedOnly
            return item
          })
        )
      } catch {
        // ignore for now
      } finally {
        if (showLoading) {
          setMetricsLoading(false)
          setBatchesLoading(false)
          loadingBar.done()
        }
      }
    },
    [loadingBar]
  )

  // Polling logic instead of SSE
  const isAnyBatchRunning = batches.some(b => b.status === 'processing')
  React.useEffect(() => {
    // Initial fetch
    loadData(page, rowsPerPage, sortBy, sortOrder, true)

    // Only set up polling if processing is active
    if (isAnyBatchRunning) {
      const interval = setInterval(() => {
        loadData(page, rowsPerPage, sortBy, sortOrder, false)
      }, 5000)
      return () => clearInterval(interval)
    }
    return undefined
  }, [page, rowsPerPage, sortBy, sortOrder, loadData, isAnyBatchRunning])

  // Countdown timer for cooldown
  React.useEffect(() => {
    if (cooldownRemaining <= 0) return

    const timer = setInterval(() => {
      setCooldownRemaining(prev => Math.max(0, prev - 1))
    }, 1000)

    return () => clearInterval(timer)
  }, [cooldownRemaining])

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  const handleStartMatching = async (batch: BatchSummary) => {
    try {
      const res = await authFetch(`${API_BASE}/api/products/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: batch.id }),
      })
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}))
        toast.error(
          data.error ??
            (data.cooldownRemainingSeconds
              ? `Cooldown: next run in ${Math.ceil((data.cooldownRemainingSeconds as number) / 60)} min`
              : 'Another process is already running.')
        )
        return
      }
      if (!res.ok) {
        toast.error('Failed to start matching')
        return
      }
      // Matching status will be reflected on next poll
      loadData(page, rowsPerPage, sortBy, sortOrder, false)
    } catch {
      toast.error('Failed to start matching')
    }
  }

  const handleRetryFailedMatching = async (batch: BatchSummary) => {
    try {
      const res = await authFetch(`${API_BASE}/api/products/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: batch.id, retry_failed_only: true }),
      })
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}))
        toast.error(
          data.error ??
            (data.cooldownRemainingSeconds
              ? `Cooldown: next run in ${Math.ceil((data.cooldownRemainingSeconds as number) / 60)} min`
              : 'Another process is already running.')
        )
        return
      }
      if (!res.ok) {
        toast.error('Failed to re-run failed requests')
        return
      }
      toast.success('Re-running failed requests for this batch')
      loadData(page, rowsPerPage, sortBy, sortOrder, false)
    } catch {
      toast.error('Failed to re-run failed requests')
    }
  }

  const handleStopMatching = async (batch: BatchSummary) => {
    try {
      await authFetch(`${API_BASE}/api/products/stop-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: batch.id }),
      })
      loadData(page, rowsPerPage, sortBy, sortOrder, false)
    } catch {
      // Ignore for now; could add toast/error UI later
    }
  }

  const handleBatchClick = (batchId: number) => {
    navigate(`/dashboard/batch/${batchId}`)
  }

  return (
    <div className='flex h-screen bg-background text-foreground font-sans overflow-hidden'>
      <Sidebar
        active='dashboard'
        onNavigate={section => {
          if (section === 'dashboard') navigate('/dashboard')
          else if (section === 'settings') navigate('/settings')
          else if (section === 'profile') navigate('/profile')
        }}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />

      <main className='flex-1 overflow-auto relative min-w-0'>
        <div className='absolute inset-0 bg-gradient-to-tr from-blue-100/50 via-background to-purple-100/50 pointer-events-none' />

        <header className='h-auto min-h-[4.5rem] border-b border-border flex flex-row items-center justify-between gap-2 sm:gap-3 px-3 sm:px-6 lg:px-8 py-3 sm:py-4 bg-white/90 backdrop-blur-md sticky top-0 z-10 transition-all shadow-sm'>
          <div className='flex items-center gap-2 sm:gap-3 min-w-0'>
            <button
              type='button'
              onClick={() => setSidebarOpen(true)}
              className='lg:hidden p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 text-muted-foreground hover:text-foreground shrink-0'
              aria-label='Open menu'
            >
              <Menu size={20} className='sm:w-6 sm:h-6' />
            </button>
            <div className='min-w-0 flex-1'>
              <h1 className='text-lg sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-foreground to-muted-foreground truncate'>
                Dashboard
              </h1>
              <p className='text-muted-foreground text-[10px] sm:text-sm truncate'>
                Welcome back, {user?.name ?? user?.email ?? 'User'}
              </p>
            </div>
          </div>

          <div className='flex items-center shrink-0'>
            <button
              type='button'
              onClick={() => setIsUploadModalOpen(true)}
              className='inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold shadow-lg hover:shadow-xl hover:from-blue-500 hover:to-purple-500 transition-all'
            >
              <PlusCircle size={18} />
              <span className='hidden sm:inline'>Upload New Batch</span>
              <span className='sm:hidden'>Upload</span>
            </button>
          </div>
        </header>

        <div className='p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 sm:space-y-8 relative z-0'>
          {cooldownRemaining > 0 && (
            <div className='rounded-2xl border border-amber-200 bg-amber-50/80 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm'>
              <div className='flex items-center justify-center h-10 w-10 rounded-xl bg-amber-100 text-amber-700 shrink-0'>
                <Timer className='h-5 w-5' />
              </div>
              <div className='flex-1'>
                <h2 className='text-sm font-semibold text-amber-900'>
                  Processing Cooldown Active
                </h2>
                <p className='text-xs text-amber-800 mt-0.5'>
                  Matching is currently on cooldown. You can start a new batch
                  in{' '}
                  <span className='font-bold'>
                    {Math.ceil(cooldownRemaining / 60)} minutes
                  </span>{' '}
                  ({cooldownRemaining} seconds).
                </p>
              </div>
            </div>
          )}

          <GlobalMetricsCards metrics={metrics} loading={metricsLoading} />

          <section aria-label='All batches' className='min-w-0'>
            <div className='flex flex-col sm:flex-row sm:flex-wrap sm:items-center justify-between mb-4 gap-3'>
              <div className='min-w-0'>
                <h2 className='text-base sm:text-lg font-semibold text-foreground'>
                  All Batches
                </h2>
                <p className='text-xs sm:text-sm text-muted-foreground'>
                  Showing recent batches. Click a row to see batch details.
                </p>
              </div>
              <div className='flex items-center gap-3 shrink-0'>
                <RowsPerPageDropdown
                  value={rowsPerPage}
                  onChange={val => {
                    setRowsPerPage(val)
                    setPage(1)
                  }}
                />
              </div>
            </div>
            <BatchListingTable
              batches={batches}
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onBatchClick={handleBatchClick}
              onStartMatching={handleStartMatching}
              onStopMatching={handleStopMatching}
              onRetryFailedMatching={handleRetryFailedMatching}
              isAnyProcessRunning={batches.some(b => b.status === 'processing')}
              cooldownRemainingSeconds={cooldownRemaining}
              statusBatchesByBatchId={statusBatches.reduce(
                (acc, b) => {
                  acc[b.batchId] = b
                  return acc
                },
                {} as Record<number, { retryFailedOnly?: boolean }>
              )}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={column => {
                if (column !== 'createdAt' && column !== 'id') return
                setSortBy(column)
                setSortOrder(prev =>
                  prev === 'asc' && column === sortBy ? 'desc' : 'asc'
                )
                setPage(1)
              }}
            />
            {batchesLoading && (
              <div className='mt-4 text-sm text-muted-foreground'>
                Refreshing batch data...
              </div>
            )}
          </section>
        </div>

        <UploadBatchModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUploadSuccess={() => {
            loadData(1, rowsPerPage, sortBy, sortOrder, true)
          }}
          onUploadError={() => {}}
        />
      </main>
    </div>
  )
}

export default DashboardOverviewPage
